/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

/**
 * FloatingCallbackWidget — Widget flottant en bas à droite qui propose
 * un rappel rapide. Apparaît après 4 secondes pour éviter d'intercepter
 * un visiteur encore en train de lire le hero.
 *
 * Click → ouvre une carte popover compacte (téléphone + email) qui
 * soumet au même endpoint que le QuickLeadForm.
 */
export const FloatingCallbackWidget = () => {
    const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 4000);
        return () => clearTimeout(t);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email && !phone) {
            setError('Indiquez au moins un moyen de vous joindre.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await api.post('/contact/leadmagnet', {
                email: (email || `callback+${Date.now()}@homly.pro`).trim().toLowerCase(),
                first_name: `[callback_widget] tel: ${phone || 'n/a'}`,
            });
            setSubmitted(true);
        } catch (err) {
            setError("Impossible d'envoyer. Réessayez dans un instant.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!mounted || dismissed) return null;

    return (
        <div className="fixed bottom-4 right-4 z-40 sm:bottom-6 sm:right-6" data-testid="floating-callback-widget">
            <AnimatePresence mode="wait">
                {open ? (
                    <motion.div
                        key="open-card"
                        initial={{ opacity: 0, y: 12, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.95 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className="w-[320px] rounded-2xl border border-white/15 bg-[#0E0918] shadow-2xl shadow-black/60 overflow-hidden"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                                    <Phone className="w-3.5 h-3.5 text-white" />
                                </div>
                                <span className="text-sm font-semibold text-white" style={DISPLAY}>
                                    Être rappelé
                                </span>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Fermer"
                                data-testid="callback-widget-close"
                                className="w-7 h-7 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/5"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4">
                            {submitted ? (
                                <div className="text-center py-3" data-testid="callback-widget-success">
                                    <CheckCircle2 className="w-9 h-9 text-emerald-300 mx-auto mb-2" />
                                    <p className="text-sm font-semibold text-white" style={DISPLAY}>
                                        Demande envoyée.
                                    </p>
                                    <p className="text-xs text-[#A19DAB] mt-1">
                                        Nous vous rappelons sous 24h ouvrées.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-2.5">
                                    <p className="text-xs text-[#A19DAB] leading-relaxed">
                                        Laissez vos coordonnées, un membre de notre équipe vous rappelle dans la journée.
                                    </p>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="Téléphone"
                                        aria-label="Téléphone"
                                        data-testid="callback-widget-phone"
                                        className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#A855F7]/50 outline-none text-sm text-white placeholder-white/35"
                                    />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email (optionnel)"
                                        aria-label="Email"
                                        data-testid="callback-widget-email"
                                        className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#A855F7]/50 outline-none text-sm text-white placeholder-white/35"
                                    />
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="callback-widget-submit"
                                        className="group w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold text-sm hover:opacity-95 transition-all disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                Demander un rappel
                                                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                                            </>
                                        )}
                                    </button>
                                    {error && <p className="text-xs text-rose-300">{error}</p>}
                                    <p className="text-[10px] text-white/35 text-center" style={MONO}>
                                        Sans engagement
                                    </p>
                                </form>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="closed-bubble"
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="relative"
                    >
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="callback-widget-open"
                            aria-label="Demander un rappel"
                            className="group flex items-center gap-2.5 pl-3 pr-5 py-3 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold text-sm shadow-2xl shadow-[#681793]/40 hover:scale-[1.04] transition-transform"
                        >
                            <span className="relative flex">
                                <span className="absolute inline-flex w-7 h-7 rounded-full bg-white/30 opacity-70 animate-ping" />
                                <span className="relative w-7 h-7 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                    <Phone className="w-3.5 h-3.5 text-white" />
                                </span>
                            </span>
                            Être rappelé
                        </button>
                        <button
                            onClick={() => setDismissed(true)}
                            aria-label="Fermer"
                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white/15 hover:bg-white/30 text-white/80 text-[10px] flex items-center justify-center backdrop-blur"
                        >
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
